// @snake-case-api Terraform variables and persisted environment configuration.
import type {
  CloudEnvironment,
  CreateEnvironmentRequest,
  CreateOperationRequest,
} from '@/core/infra_types'
import type { CloudCredential } from '@/features/secondary/infra_credential.service'
import type { PendingHost } from './infra_governance.service'
import {
  azureNetworkVariables,
  defaultAzureNetwork,
  ipv4Prefix,
  validAzureNetwork,
  type AzureNetwork,
} from './vmNetwork'
import { isReleaseVersion } from '@/core/infra_semver'
import { canEnableJinaReranker, upcloudPlanStorageGb } from './infra_vm_sizes'

export type VmProvider = 'azure' | 'gcp' | 'upcloud'
export type SshAccessMode = 'vpn_only' | 'private_network' | 'legacy'
export interface VmDraft extends AzureNetwork {
  requestId: string
  hostId: string
  provider: VmProvider
  credentialId: string
  region: string
  vmSize: string
  resourceGroupName: string
  bastionEnabled: boolean
  moduleVersion: string
  moduleDigest: string
  terraformVersion: string
  imageName: string
  sshKeyId: string
  sshAllowedCidrs: string
  sshAccessMode: SshAccessMode
  managedDbEnabled: boolean
  dbTier: string
  dbDiskSizeGb: string
  separateIntelligenceDb: boolean
  intelligenceDbTier: string
  jinaRerankerEnabled: boolean
  diskSizeGb: string
  dfsEnabled: boolean
  dfsReplication: string
  diagnosticsEnabled: boolean
  logRetentionDays: string
  zone: string
  availabilityZones: string[]
}

export function hostProvider(host: PendingHost): VmProvider | undefined {
  const value = host.metadata?.['cloudProvider'] ?? host.metadata?.['cloud_provider']
  return value === 'azure' || value === 'gcp' || value === 'upcloud' ? value : undefined
}

export function credentialDestination(credential?: CloudCredential): string {
  const metadata = credential?.metadata
  return String(
    metadata?.['subscriptionId'] ??
      metadata?.['subscription_id'] ??
      metadata?.['projectId'] ??
      metadata?.['project_id'] ??
      ''
  )
}

/** Azure requires an immutable gallery definition/version or full resource ID. */
export function vmImageReference(
  provider: VmProvider | undefined,
  image: { name: string; version: string; imageId?: string }
): string {
  if (provider === 'azure' && image.imageId?.startsWith('/communityGalleries/')) return image.imageId
  return provider === 'azure' && !image.name.includes('/')
    ? `${image.name}/${image.version}`
    : image.name
}

/**
 * Providers whose terraform module builds exactly one VM. azure-vm and
 * upcloud-vm both declare a single server with no count, so a host group on
 * either runs one host however many replicas the operator asks for. The
 * infra-service enqueue guard refuses the same counts; this keeps the wizard
 * from promising what the module cannot build.
 */
export function isSingletonVmProvider(provider: string | undefined): boolean {
  return provider === 'azure' || provider === 'upcloud'
}

/**
 * Boot disk limits, per provider, matching infra-service vmconfig.DiskBounds.
 *
 * Each floor is the size that cloud's Agion Packer image is built at, because
 * a smaller boot disk cannot hold it: byoh-agent-image/gcp.pkr.hcl builds at
 * 20 GB, azure.pkr.hcl at 30 GB, and the UpCloud templates are 25 GB.
 *
 * Each ceiling is the largest boot disk the cloud accepts for the disk type
 * the module asks for. gcp-vm asks for pd-ssd and Compute Engine Persistent
 * Disk volumes stop at 64 TiB = 65536 GB. azure-vm asks for Premium_LRS and
 * an Azure OS disk stops at 4095 GiB. upcloud-vm sizes an upcloud_server
 * template, which the UpCloud provider validates at 10..2048 GB — below
 * UpCloud's documented 4 TB per storage device, so 2048 binds here.
 */
const bootDiskBounds: Record<VmProvider, { min: number; max: number }> = {
  gcp: { min: 20, max: 65536 },
  azure: { min: 30, max: 4095 },
  upcloud: { min: 25, max: 2048 },
}

/** An unknown provider gets the narrowest bounds, so nothing slips past. */
export function diskSizeBounds(provider: VmProvider | undefined): { min: number; max: number } {
  return provider ? bootDiskBounds[provider] : { min: 30, max: 2048 }
}

/** A new UpCloud VM starts with the storage its plan already pays for. */
export function includedDiskSizeGb(provider: VmProvider | undefined, vmSize: string): string {
  const size = provider === 'upcloud' ? upcloudPlanStorageGb(vmSize) : undefined
  return size === undefined ? '' : String(size)
}

/**
 * Reproduce the azure-vm module's own sanitizer character for character:
 *   local.safe_rg_name = replace(lower(var.resource_group_name), "/[^a-z0-9-_.()]/", "-")
 * (azure-vm identity.tf:5, consumed by network.tf:8 and every resource that
 * takes resource_group_name from it).
 *
 * azurerm_resource_group.name is ForceNew, so a panel rule that differs from
 * the module's by one character renames the resource group and destroys it
 * with all of its children. The two rules used to diverge on a double space,
 * an underscore, a dot, parentheses and trailing punctuation: the panel
 * collapsed runs of non-alphanumerics to one dash, dropped `_ . ( )` and
 * trimmed edge dashes, while the module replaces each disallowed character
 * on its own and keeps `_ . ( )`.
 *
 * Sending the sanitized form is what the module would compute anyway, because
 * its output contains only characters the rule allows.
 */
export function azureSafeName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9\-_.()]/g, '-')
}

/**
 * Resource group a re-apply must target. The recorded name is what the last
 * apply sent, so it is the one the module consumed; the derivation is only
 * reached before any apply has written it back, when no resource group of
 * this environment exists yet.
 */
export function azureResourceGroupName(recorded: string, environmentName: string): string {
  return azureSafeName(recorded.trim() || `agion-${environmentName}`)
}

/** Size the last successful apply recorded. The last submission may have failed. */
export function appliedDiskSizeGb(env: CloudEnvironment): number | undefined {
  const value = String(env.config['diskSizeGb'] ?? env.config['disk_size_gb'] ?? '')
  return /^[1-9]\d*$/.test(value) ? Number(value) : undefined
}

/**
 * Whether leaving the boot disk field empty keeps the disk the machine runs
 * on. True only where something fills the variable in: upcloud-vm's apply
 * worker reads the live disk from UpCloud (guardVMDisk), and for gcp-vm and
 * azure-vm diskSizeToApply resends the recorded size.
 *
 * With nothing recorded on those two, an empty field asks for the module
 * default of 50 GB instead, which rebuilds a gcp-vm around a smaller disk and
 * fails an azure-vm apply outright. Copy that promises the current disk has to
 * ask this, not whether the operator is re-applying.
 */
export function emptyDiskKeepsCurrent(
  provider: VmProvider | undefined,
  reapply: boolean,
  appliedDiskGb: number | undefined,
): boolean {
  if (!reapply) return false
  return provider === 'upcloud' || appliedDiskGb !== undefined
}

export function newVmDraft(host: PendingHost, requestId: string = crypto.randomUUID()): VmDraft {
  const provider = hostProvider(host)
  if (!provider) throw new Error('Unsupported single-VM provider')
  const vmSize =
    provider === 'azure'
      ? 'Standard_D4s_v5'
      : provider === 'gcp'
        ? 'e2-standard-4'
        : 'PREMIUM-4xCPU-16GB'
  return {
    ...defaultAzureNetwork,
    requestId,
    hostId: host.id,
    provider,
    credentialId: '',
    region:
      provider === 'azure' ? 'swedencentral' : provider === 'gcp' ? 'europe-north1' : 'fi-hel1',
    vmSize,
    moduleVersion: '',
    resourceGroupName: '',
    bastionEnabled: true,
    moduleDigest: '',
    terraformVersion: '',
    imageName: '',
    sshKeyId: '',
    sshAllowedCidrs: '',
    sshAccessMode: 'vpn_only',
    managedDbEnabled: false,
    dbTier:
      provider === 'azure'
        ? 'B_Standard_B2s'
        : provider === 'gcp'
          ? 'db-custom-2-8192'
          : '1x2xCPU-4GB-50GB',
    dbDiskSizeGb: '',
    separateIntelligenceDb: false,
    intelligenceDbTier: '',
    jinaRerankerEnabled: false,
    diskSizeGb: includedDiskSizeGb(provider, vmSize),
    dfsEnabled: true,
    dfsReplication: 'LRS',
    diagnosticsEnabled: true,
    logRetentionDays: '30',
    zone: '',
    availabilityZones: [],
  }
}

export type VmDraftError = 'credential' | 'compute' | 'release' | 'image' | 'network' | 'access' | 'accessRelease' | 'storage' | 'diskShrink' | 'diagnostics' | 'database' | 'databaseDisk' | 'sshKeyAzure'

export function validSshAccess(mode: SshAccessMode, sources: string): boolean {
  if (mode === 'vpn_only' || mode === 'legacy') return true
  return mode === 'private_network' && sources.split(',').every((source) => {
    const prefix = ipv4Prefix(source.trim())
    return prefix !== null && prefix.bits > 0
  })
}

/** appliedDiskGb is the current UpCloud disk on re-apply; UpCloud cannot shrink it. */
export function vmDraftErrors(draft: VmDraft, appliedDiskGb?: number): VmDraftError[] {
  const errors: VmDraftError[] = []
  if (!draft.credentialId) errors.push('credential')
  if (
    !draft.region ||
    !draft.vmSize ||
    (draft.provider === 'azure' && !/^Standard_[A-Za-z0-9_]+$/.test(draft.vmSize))
  )
    errors.push('compute')
  if (!isReleaseVersion(draft.moduleVersion) || !/^sha256:[a-f0-9]{64}$/.test(draft.moduleDigest))
    errors.push('release')
  if (!draft.imageName) errors.push('image')
  if (draft.provider === 'azure' && !validAzureNetwork(draft)) errors.push('network')
  if (draft.provider === 'gcp' && !ipv4Prefix(draft.networkCidr)) errors.push('network')
  if (
    draft.provider === 'azure' &&
    !validSshAccess(draft.sshAccessMode, draft.sshAllowedCidrs)
  )
    errors.push('access')
  if (draft.diskSizeGb) {
    const size = Number(draft.diskSizeGb)
    const { min, max } = diskSizeBounds(draft.provider)
    if (!/^\d+$/.test(draft.diskSizeGb) || size < min || size > max) errors.push('storage')
    else if (draft.provider === 'upcloud' && appliedDiskGb !== undefined && size < appliedDiskGb)
      errors.push('diskShrink')
  }
  if (
    draft.provider === 'azure' &&
    draft.diagnosticsEnabled &&
    (!/^\d+$/.test(draft.logRetentionDays) ||
      Number(draft.logRetentionDays) < 30 ||
      Number(draft.logRetentionDays) > 730)
  )
    errors.push('diagnostics')
  if (
    draft.managedDbEnabled &&
    (!draft.dbTier || (draft.separateIntelligenceDb && !draft.intelligenceDbTier))
  )
    errors.push('database')
  if (draft.provider === 'gcp' && draft.managedDbEnabled && draft.dbDiskSizeGb &&
    (!/^[1-9]\d*$/.test(draft.dbDiskSizeGb) || !Number.isSafeInteger(Number(draft.dbDiskSizeGb))))
    errors.push('databaseDisk')
  return errors
}

export function buildVmEnvironment(draft: VmDraft, host: PendingHost): CreateEnvironmentRequest {
  if (
    draft.hostId !== host.id ||
    draft.provider !== hostProvider(host) ||
    vmDraftErrors(draft).length
  )
    throw new Error('Complete the VM configuration before provisioning')
  return {
    requestId: draft.requestId,
    name: host.name,
    cloudProvider: draft.provider,
    region: draft.region,
    deploymentModel: 'vm_single',
    credentialId: draft.credentialId,
    config: {
      ...draft,
      host_id: host.id,
      vm_size: draft.vmSize,
      tf_module_version: draft.moduleVersion,
      onboarding_draft: draft,
      ...azureNetworkVariables(draft),
    },
  }
}

/**
 * Boot disk size to send. Every single-VM module declares disk_size_gb with a
 * default of 50 (azure-vm variables.tf:45, gcp-vm variables.tf:127,
 * upcloud-vm variables.tf:22), so omitting the variable on a re-apply does
 * not keep the current disk: it asks for 50 GB, and on azure-vm and gcp-vm
 * that rebuilds the VM around a smaller disk. Resend the size the last
 * successful apply recorded instead.
 *
 * upcloud-vm keeps omitting it: its apply worker reads the live disk from
 * UpCloud and fills the variable in (guardVMDisk), which is closer to the
 * truth than any stored record.
 */
function diskSizeToApply(draft: VmDraft, env: CloudEnvironment, mode: 'create' | 'reapply'): string {
  if (draft.diskSizeGb || mode !== 'reapply' || draft.provider === 'upcloud') return draft.diskSizeGb
  const applied = appliedDiskSizeGb(env)
  return applied === undefined ? '' : String(applied)
}

/** No secrets: the worker resolves host bootstrap and cloud credentials. */
export function buildVmOperation(
  draft: VmDraft,
  env: CloudEnvironment,
  credential: CloudCredential,
  mode: 'create' | 'reapply' = 'create'
): CreateOperationRequest {
  if (
    vmDraftErrors(draft, mode === 'reapply' ? appliedDiskSizeGb(env) : undefined).length ||
    credential.id !== draft.credentialId ||
    credential.cloudProvider !== draft.provider
  )
    throw new Error('Deployment configuration no longer matches the selected credential')
  const diskSize = diskSizeToApply(draft, env, mode)
  const variables: Record<string, string> = {
    module_version: draft.moduleVersion,
    module_digest: draft.moduleDigest,
    environment_name: env.name,
    vm_size: draft.vmSize,
    replica_count: '1',
    managed_db_enabled: String(draft.managedDbEnabled),
    separate_intelligence_db: String(draft.managedDbEnabled && draft.separateIntelligenceDb),
    jina_reranker_enabled: String(draft.jinaRerankerEnabled && canEnableJinaReranker(draft.vmSize)),
    dfs_enabled: String(draft.dfsEnabled),
    ...(diskSize ? { disk_size_gb: diskSize } : {}),
  }
  if (draft.provider === 'azure')
    Object.assign(variables, {
      ...azureNetworkVariables(draft),
      location: draft.region,
      resource_group_name: azureResourceGroupName(
        String(draft.resourceGroupName || env.config['resourceGroupName'] || env.config['resource_group_name'] || ''),
        env.name
      ),
      subscription_id: credentialDestination(credential),
      image_name: draft.imageName,
      db_sku: draft.dbTier,
      intelligence_db_sku: draft.intelligenceDbTier || draft.dbTier,
      availability_zones_json: JSON.stringify(draft.availabilityZones),
      ssh_access_mode: draft.sshAccessMode,
      ssh_allowed_cidrs_json: JSON.stringify(
        draft.sshAccessMode === 'vpn_only' ? [] : (draft.sshAllowedCidrs || (draft.sshAccessMode === 'legacy' ? '*' : ''))
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      ),
      dfs_replication: draft.dfsReplication,
      diagnostics_enabled: String(draft.diagnosticsEnabled),
      log_retention_days: draft.logRetentionDays,
    })
  if (draft.provider === 'gcp')
    Object.assign(variables, {
      project_id: credentialDestination(credential),
      region: draft.region,
      network_cidr: draft.networkCidr,
      ha_mode: 'standalone',
      bastion_enabled: String(draft.bastionEnabled),
      image_name: draft.imageName,
      db_tier: draft.dbTier,
      ...(draft.managedDbEnabled && draft.dbDiskSizeGb ? { db_disk_size_gb: draft.dbDiskSizeGb } : {}),
      intelligence_db_tier: draft.intelligenceDbTier || draft.dbTier,
      ...(draft.zone ? { zone: draft.zone } : {}),
    })
  if (draft.provider === 'upcloud')
    Object.assign(variables, {
      zone: draft.region,
      plan: draft.vmSize,
      template_name: draft.imageName,
      db_plan: draft.dbTier,
      intelligence_db_plan: draft.intelligenceDbTier || draft.dbTier,
    })
  return {
    requestId: draft.requestId,
    environmentId: env.id,
    operationType: 'terraform_apply',
    parameters: { module: `${draft.provider}-vm`, variables, resolve_host_bootstrap: true,
      ...(mode === 'reapply' ? { submission_mode: 'reapply' } : {}),
    },
  }
}
